import React, { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";

import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { fetchCirclePostDetail } from "../../../states/actions/fetchCirclePostDetail";
import {
  doObjToFormData,
  getIpAddress,
  postDateFormat,
  timeAgo,
} from "../../../helpers/helpers";
import http from "../../../helpers/http";
import paths from "../../../constants/paths";
import { TOAST_SETTINGS } from "../../../utils/siteSettings";

import LoadingScreen from "../../common/LoadingScreen";
import Text from "../../common/Text";
import ImageControl from "../../common/ImageControl";
import CommentFeedBack from "../../common/CommentFeedBack";
import FormProcessingSpinner from "../../common/FormProcessingSpinner";
import ReportPopup2 from "../../common/ReportPopup2";
import { getAuthUserToken } from "../../../helpers/authToken";

const CircleDetail = () => {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const actionRef = useRef(null);

  const data = useSelector((state) => state.fetchCirclePostDetail.content);
  const isLoading = useSelector(
    (state) => state.fetchCirclePostDetail.isLoading
  );
  const isSiteSettingsLoading = useSelector(
    (state) => state.fetchSiteSettings.isLoading
  );
  // const activePlan = useSelector((state) => state.fetchSiteSettings.activePlan);

  const { post, comments, category, category_slug } = data;

  const [typeStatus, setTypeStatus] = useState("unlike");
  const [likeCount, setLikeCount] = useState(0);
  const [ipAddress, setIpAddress] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reply, setReply] = useState(false);
  const [activeReplyCommentId, setActiveReplyCommentId] = useState(null);
  const [isReportPopupOpen, setIsReportPopupOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [activeActionPostId, setActiveActionPostId] = useState(null);

  const authToken = getAuthUserToken();

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
    reset,
  } = useForm();

  // useEffect(() => {
  //   if (!isSiteSettingsLoading) {
  //     if (!activePlan || [1, 4, 5, 7].includes(Number(activePlan?.plan_id))) {
  //       window.location.href = `${paths.SITE_BASE_URL}/subscription`;
  //     }
  //   }
  // }, [isSiteSettingsLoading, activePlan]);
  useEffect(() => {
    dispatch(fetchCirclePostDetail({ slug }));
  }, []);

  useEffect(() => {
    if (post) {
      setTypeStatus(post?.like_status == 1 ? "like" : "unlike");
      setLikeCount(post?.likes || 0);
    }
  }, [post]);

  useEffect(() => {
    const fetchAndTrackView = async () => {
      try {
        const userIp = await getIpAddress();
        setIpAddress(userIp);

        if (post?.id && userIp) {
          const formData = {
            post_id: post.id,
            ip_address: userIp,
            token: authToken,
          };
          await http.post("track-circle-views", doObjToFormData(formData));
        }
      } catch (error) {
        toast.error("Error fetching or sending IP:", error);
      }
    };

    fetchAndTrackView();
  }, [post?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionRef.current && !actionRef.current.contains(event.target)) {
        setActiveActionPostId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useDocumentTitle(
    post?.title && data?.site_name
      ? `${post.title} - ${data.site_name}`
      : "Loading..."
  );

  const handleStats = (type) => {
    if (authToken) {
      const formData = {
        post_id: post?.id,
        type,
        token: authToken,
      };
      try {
        http
          .post("save-circle-stats", doObjToFormData(formData))
          .then((response) => {
            const data = response.data || response;
            if (data.status == 1) {
              setTypeStatus(
                data.type_status === "like" ? "like" : post?.like_status
              );
              setLikeCount(data.count_likes);
            } else {
              toast.error("Technical Error", TOAST_SETTINGS);
            }
          });
      } catch (errors) {
        console.log("Errors", errors);
      }
    } else {
      window.location.replace(paths.SITE_BASE_URL + "/signin");
    }
  };

  const handleToggleReportPopup = (post = null) => {
    setSelectedPost(post);
    setIsReportPopupOpen((prev) => !prev);
  };

  const ActionToggle = (postId) => {
    setActiveActionPostId(postId);
  };

  const ActionReply = () => setReply(true);
  const HideReply = () => {
    setReply(false);
    clearErrors("msg");
  };

  const handleReplyClick = (commentId) => {
    if (activeReplyCommentId !== commentId) {
      reset({ reply_msg: "" });
    }
    setActiveReplyCommentId((prevId) =>
      prevId === commentId ? null : commentId
    );
  };

  const handleReplyCancel = () => {
    setActiveReplyCommentId(null);
    clearErrors("reply_msg");
  };

  const handleCommentSubmit = async (data, e) => {
    e.preventDefault();
    if (!authToken) {
      window.location.replace(paths.SITE_BASE_URL + "/signin");
    }
    try {
      setIsSubmitting(true);

      const formPayload = {
        token: authToken,
        post_id: post?.id,
        comment_id: activeReplyCommentId || 0,
        ...(activeReplyCommentId
          ? { reply_msg: data.reply_msg }
          : { msg: data.msg }),
      };

      const response = await http.post(
        "save-circle-post-comments",
        doObjToFormData(formPayload)
      );
      const res = response.data || response;

      if (res.status === 1) {
        toast.success(res.msg, TOAST_SETTINGS);
        setTimeout(() => {
          setReply(false);
          setActiveReplyCommentId(null);
          reset();
          dispatch(fetchCirclePostDetail({ slug }));
          setIsSubmitting(false);
        }, 2000);
      } else {
        toast.error(res.msg, TOAST_SETTINGS);
        setIsSubmitting(false);
      }
    } catch (error) {
      toast.error("Something went wrong!", TOAST_SETTINGS);
      console.error("handleCommentSubmit error:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isLoading || isSiteSettingsLoading ? (
        <LoadingScreen />
      ) : (
        <>
          <ToastContainer />
          {isReportPopupOpen && (
            <ReportPopup2
              handleToggleReportPopup={() => handleToggleReportPopup(null)}
              post={selectedPost}
            />
          )}
          <section className="forum_banners forum_detail">
            <div className="contain">
              <div className="content_lbl">
                <h1 className="heading">{post.title}</h1>
                {/* <p> */}
                <Link to="/learning-hub">Learning Hub</Link> <span>/</span>{" "}
                <Link to={`/learning-hub?category=${category_slug}`}>
                  {category}
                </Link>{" "}
                {/* </p> */}
              </div>
              <div className="flex posts">
                {/* Right Content */}
                <div className="colR">
                  {/* Posts */}
                  <div className="forum_posts_outer">
                    <div className="inner">
                      {/* Single Post */}
                      <div className="post_header">
                        <div className="post_title">
                          <h4>
                            <Link to="">{post.title}</Link>
                          </h4>
                          <div className="post_tags">
                            {post?.tags &&
                              post.tags
                                .split(",")
                                .map((tag, i) => (
                                  <span key={i}>{tag.trim()}</span>
                                ))}
                          </div>
                        </div>
                        <div className="post_date_action">
                          <div className="post_date">
                            {" "}
                            {postDateFormat(post.created_date) || "N/A"}
                          </div>
                          <div className="post_action" ref={actionRef}>
                            <button
                              type="button"
                              onClick={() => ActionToggle(post.id)}
                            >
                              <img
                                src={paths.SITE_BASE_URL + "/images/action.svg"}
                                alt="action"
                                width="100"
                                height="100"
                              />
                            </button>
                            {activeActionPostId === post.id && (
                              <div className="action_drop">
                                <a
                                  href="javascript:void(0)"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleToggleReportPopup(post);
                                  }}
                                >
                                  Report
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="post_description">
                        <Text string={post.description} parse={true} />{" "}
                        {post.image && (
                          <div className="image">
                            <ImageControl
                              folder="circles"
                              src={post.image}
                              //   specificWidth="400p_"
                            />
                            {/* <img src={circle.image} alt="Post visual" /> */}
                          </div>
                        )}
                      </div>

                      <div className="post_footer">
                        <Link to="">
                          <div className="post_footer_action">
                            <img
                              src={paths.SITE_BASE_URL + "/images/view.svg"}
                              alt="views"
                              width="100"
                              height="100"
                            />
                            <span>{post.views}</span>
                          </div>
                        </Link>
                        <div className="post_footer_action">
                          <img
                            src={
                              typeStatus === "like"
                                ? paths.SITE_BASE_URL + "/images/liked.svg"
                                : paths.SITE_BASE_URL + "/images/like.svg"
                            }
                            alt="likes"
                            width="100"
                            height="100"
                            onClick={() => handleStats("like")}
                            style={{ cursor: "pointer" }}
                          />
                          <span>{likeCount}</span>
                        </div>
                        <Link to="">
                          <div className="post_footer_action">
                            <img
                              src={paths.SITE_BASE_URL + "/images/comment.svg"}
                              alt="comments"
                              width="100"
                              height="100"
                            />
                            <span>{post.comments}</span>
                          </div>
                        </Link>
                      </div>
                    </div>
                    <div className="reply_blk">
                      <div className="cmnt_head">
                        <div className="cmnt_ask">
                          <h4>Comments</h4>
                          <div className="btn_blk">
                            <button
                              type="button"
                              className={
                                reply ? "site_btn blank hide" : "site_btn blank"
                              }
                              onClick={ActionReply}
                            >
                              <span>Add Comment</span>
                              <img
                                src={
                                  paths.SITE_BASE_URL + "/images/PlusSquare.svg"
                                }
                                alt=""
                              />
                            </button>
                          </div>
                        </div>

                        {reply && (
                          <form
                            method="POST"
                            onSubmit={handleSubmit(handleCommentSubmit)}
                          >
                            <div className="reply_form">
                              <label htmlFor="comment">Add Comment</label>
                              <textarea
                                id="comment"
                                className="input"
                                // placeholder="Your answer here. Something like this Lorem ipsum dolor sit amet consectetur. Arcu nunc quam non risus hac lacus sem amet. Fermentum purus eleifend scelerisque metus arcu a porta in at. Tellus hac volutpat a nulla malesuada non auctor tincidunt ante. Enim adipiscing sagittis nullam tellus tincidunt."
                                {...register("msg", {
                                  required: "Comment is required",
                                })}
                              ></textarea>
                              <div
                                className="validation-error"
                                style={{ color: "red" }}
                              >
                                {errors.msg?.message}
                              </div>

                              <div className="btn_blk text-left mt-3">
                                <button
                                  className="site_btn"
                                  type="submit"
                                  disabled={isSubmitting}
                                >
                                  {isSubmitting ? (
                                    <>
                                      <FormProcessingSpinner
                                        isFormProcessing={true}
                                      />
                                      <span>Submit</span>
                                    </>
                                  ) : (
                                    "Submit"
                                  )}
                                </button>

                                <button
                                  type="button"
                                  className="site_btn simple"
                                  onClick={HideReply}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </form>
                        )}
                      </div>

                      {comments && comments?.length > 0 && (
                        <div className="reply_area_blk">
                          {comments?.map((comment) => (
                            <div key={comment?.id}>
                              <div className="main_auth" key={comment?.id}>
                                <div className="post_auther">
                                  <div className="image">
                                    {comment?.author_image ? (
                                      <ImageControl
                                        folder="members"
                                        src={comment.author_image}
                                      />
                                    ) : (
                                      <img
                                        alt="author"
                                        loading="lazy"
                                        width="100"
                                        height="100"
                                        decoding="async"
                                        src={
                                          paths.SITE_BASE_URL +
                                          "/images/no-user.svg"
                                        }
                                        style={{ color: "transparent" }}
                                      />
                                    )}
                                  </div>

                                  <div className="auther_info">
                                    <div>
                                      <h5>
                                        {comment?.author_name ||
                                          "User name here"}
                                      </h5>
                                      <p>
                                        <span>
                                          <span className="time">
                                            {timeAgo(comment?.created_date)}
                                          </span>{" "}
                                        </span>
                                      </p>
                                      <div className="post_description">
                                        <p>{comment?.msg}</p>
                                      </div>
                                      <div className="post_footer">
                                        <CommentFeedBack
                                          comment={comment}
                                          post={post}
                                        />
                                        {!comment?.reply && (
                                          <div className="post_footer_action reply_btn">
                                            <img
                                              alt="reply"
                                              loading="lazy"
                                              width="100"
                                              height="100"
                                              decoding="async"
                                              src={
                                                paths.SITE_BASE_URL +
                                                "/images/comment.svg"
                                              }
                                              style={{ color: "transparent" }}
                                            />
                                            <span
                                              className="simple reply_btn"
                                              style={{ cursor: "pointer" }}
                                              onClick={() =>
                                                handleReplyClick(comment?.id)
                                              }
                                            >
                                              Reply
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {activeReplyCommentId === comment?.id && (
                                      <form
                                        onSubmit={handleSubmit(
                                          handleCommentSubmit
                                        )}
                                        className="reply_form"
                                      >
                                        <textarea
                                          className="input"
                                          placeholder="Write your reply here..."
                                          {...register("reply_msg", {
                                            required: "Reply is required",
                                          })}
                                        ></textarea>
                                        <div
                                          className="validation-error"
                                          style={{ color: "red" }}
                                        >
                                          {errors.reply_msg?.message}
                                        </div>
                                        <div className="btn_blk text-left mt-3">
                                          <button
                                            className="site_btn"
                                            type="submit"
                                            disabled={isSubmitting}
                                          >
                                            {isSubmitting ? (
                                              <>
                                                <FormProcessingSpinner
                                                  isFormProcessing={true}
                                                />
                                                <span>Submit</span>
                                              </>
                                            ) : (
                                              "Submit"
                                            )}
                                          </button>
                                          <button
                                            type="button"
                                            className="site_btn simple"
                                            onClick={handleReplyCancel}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </form>
                                    )}
                                    {comment?.reply && (
                                      <div className="post_auther reply_post_auther">
                                        <div className="image">
                                          {comment?.reply?.author_image ? (
                                            <ImageControl
                                              folder="members"
                                              src={comment?.reply?.author_image}
                                            />
                                          ) : (
                                            <img
                                              alt="author"
                                              loading="lazy"
                                              width="100"
                                              height="100"
                                              decoding="async"
                                              src={
                                                paths.SITE_BASE_URL +
                                                "/images/no-user.svg"
                                              }
                                              style={{ color: "transparent" }}
                                            />
                                          )}
                                        </div>
                                        <div className="auther_info">
                                          <div>
                                            <h5>
                                              {comment?.reply?.author_name ||
                                                "User name here"}
                                            </h5>
                                            <p>
                                              <span>
                                                <span className="time">
                                                  {timeAgo(
                                                    comment?.reply?.created_date
                                                  )}
                                                </span>{" "}
                                              </span>
                                            </p>
                                            <div className="post_description">
                                              <p>{comment?.reply?.msg}</p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {/* <div className="gap_line"></div> */}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
};

export default CircleDetail;
